/** Endpoint-owned entity revisions. The relay stores only immutable ciphertext and opaque heads. */
import {
  canonicalize,
  hashCanonicalB64,
  openContent,
  sealContent,
  utf8,
  type WebCryptoKey,
} from '@viko/crypto'
import {
  ENTITY_HEADS_PAGE_BYTES,
  ENTITY_RECORD_BYTES,
  EntityContent,
  EntityHead,
  EntityHeadsPage,
  EntityListPage,
  EntityPlaintext,
  EntityReadPage,
  type PrivateEntityKind,
} from '@viko/protocol'
import { ContentRequestError, contentRequestError, isUnprovisioned } from './content-errors.js'
import { idb } from './idb.js'

type Head = ReturnType<typeof EntityHead.parse>
type Content = ReturnType<typeof EntityContent.parse>
type Saved = {
  head?: Head
  content?: Content
  pending?: { content: Content; previous: string | null }
}
export interface EntityTransport {
  put(input: NonNullable<Saved['pending']>): Promise<unknown>
  list(input: { kind: PrivateEntityKind; after?: string }): Promise<unknown>
  read(input: { kind: PrivateEntityKind; id: string; after: number }): Promise<unknown>
  /** read-budget: every kind's heads in one call. Absent (or refused as unknown) → per-kind list. */
  heads?(input: { after?: string; since?: number }): Promise<unknown>
}
type Row = { id: string; kind: PrivateEntityKind; revision: number; value: Record<string, unknown> }
type HeadsByKind = Map<PrivateEntityKind, Head[]>

/** How long one all-kinds heads answer is shared by every list on the page. */
export const ENTITY_HEADS_TTL_MS = 5_000

/**
 * read-budget: a page load lists many entity kinds (projects, workstreams, tasks, run titles,
 * settings, …). They share ONE heads answer (`heads`, all kinds, conditional on the account's
 * entity version) for `headsTtlMs`, and only heads that changed since this session (memory) or
 * this browser (IndexedDB checkpoint) verified them are read from the relay. Concurrent reads of
 * the same entity share one request. `headsTtlMs: 0` with no `heads` transport is the legacy
 * behaviour (one list per kind and page, one read per listed head).
 */
export class BrowserEntities {
  private locked = false
  /** Verified rows of this session, keyed by kind/id: a listed head equal to one costs nothing. */
  private readonly memo = new Map<string, Row & { digest: string }>()
  /** Heads this session wrote: a heads answer that started before the write never hides it. */
  private readonly own = new Map<string, Head>()
  private readonly inflight = new Map<string, Promise<Row>>()
  private readonly lists = new Map<PrivateEntityKind, Promise<Row[]>>()
  private known: { version: number; byKind: HeadsByKind } | undefined
  private snap: { p: Promise<HeadsByKind | undefined>; at?: number } | undefined
  private headsUnsupported = false
  constructor(
    private readonly options: {
      accountId: string
      currentKey(): Promise<{ epoch: number; key: WebCryptoKey }>
      keyForEpoch(epoch: number): Promise<WebCryptoKey>
      transport: EntityTransport
      /** Endpoint check that neither the relay nor this browser's pin has a directory. */
      unprovisioned?(): Promise<boolean>
      headsTtlMs?: number
      now?: () => number
    },
  ) {}
  lock() {
    this.locked = true
    this.memo.clear()
    this.snap = undefined
    this.known = undefined
  }
  /** Another device may have written: the next list asks the relay (conditionally) again. */
  changed() {
    if (this.snap?.at !== undefined) this.snap = undefined
  }
  private check() {
    if (this.locked) throw new Error('Encrypted entities locked')
  }
  private key(kind: PrivateEntityKind, id: string) {
    return `entity-checkpoint/v1/${JSON.stringify([this.options.accountId, kind, id])}`
  }
  private async open(content: Content, kind: PrivateEntityKind, id: string) {
    this.check()
    const c = content.context
    if (
      c.accountId !== this.options.accountId ||
      c.kind !== kind ||
      c.objectId !== id ||
      c.runId !== `entity:${kind}`
    )
      throw new Error('Entity context mismatch')
    const key = await this.options.keyForEpoch(c.epoch)
    const plaintext = EntityPlaintext.parse(
      JSON.parse(new TextDecoder().decode(await openContent(key, c, content.box))),
    )
    this.check()
    return plaintext
  }
  /** One durable creation intent per kind; an uncertain create never mints a second entity. */
  async create(kind: PrivateEntityKind, value: Record<string, unknown>) {
    this.check()
    const slot = `entity-create/v1/${JSON.stringify([this.options.accountId, kind])}`
    const id = await idb.update<string>(slot, (current) => current ?? crypto.randomUUID())
    const saved = await idb.get<Saved>(this.key(kind, id))
    const existing = saved?.pending?.content ?? saved?.content
    if (existing) {
      const body = await this.open(existing, kind, id)
      const intent = (v: Record<string, unknown>) =>
        Object.fromEntries(
          Object.entries(v).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt'),
        )
      if (canonicalize(intent(body.value)) !== canonicalize(intent(value)))
        throw new Error('Finish the pending entity creation first')
      value = body.value
    }
    const result =
      saved?.head && !saved.pending
        ? { id, kind, revision: saved.head.revision, value }
        : await this.write(kind, id, value)
    this.check()
    await idb.update<string | undefined>(slot, (current) => (current === id ? undefined : current))
    return result
  }
  async read(kind: PrivateEntityKind, id: string) {
    this.check()
    const key = this.key(kind, id)
    const before = (await idb.get<Saved>(key)) ?? {}
    let head = before.head
    let content = before.content
    let advertised = before.head
    // Every intervening revision is authenticated, including its encrypted previous digest.
    for (let n = 0; n < 4096; n++) {
      const page = EntityReadPage.parse(
        await this.options.transport.read({ kind, id, after: head?.revision ?? 0 }),
      )
      this.check()
      if (
        page.head.id !== id ||
        page.head.kind !== kind ||
        page.head.revision < (head?.revision ?? 0)
      )
        throw new Error('Entity rollback or substitution')
      if (
        advertised &&
        (page.head.revision < advertised.revision ||
          (page.head.revision === advertised.revision && page.head.digest !== advertised.digest))
      )
        throw new Error('Entity advertised head rollback or fork')
      advertised = page.head
      for (const record of page.records) {
        if (
          record.context.seq !== (head?.revision ?? 0) + 1 ||
          record.context.seq > page.head.revision
        )
          throw new Error('Entity revision gap')
        // Epochs never decrease along a chain (the relay refuses stale-epoch writes): an older
        // epoch here is a forgery by a holder of a retired key, e.g. a revoked device.
        if (content && record.context.epoch < content.context.epoch)
          throw new Error('Entity epoch downgrade')
        const body = await this.open(record, kind, id)
        if (body.previous !== (head?.digest ?? null)) throw new Error('Entity revision fork')
        head = {
          id,
          kind,
          revision: record.context.seq,
          digest: await hashCanonicalB64('viko/entity-revision/v1', record),
        }
        content = record
      }
      if (head?.revision === page.head.revision) {
        if (head.digest !== page.head.digest || !content) throw new Error('Entity head fork')
        const body = await this.open(content, kind, id)
        this.check()
        await idb.update<Saved>(key, (current) => {
          this.check()
          if (JSON.stringify(current ?? {}) !== JSON.stringify(before))
            throw new Error('Entity checkpoint changed')
          return { ...before, head, content }
        })
        const row = { id, kind, revision: head.revision, value: body.value }
        this.remember(row, head.digest)
        return row
      }
      if (!page.records.length) throw new Error('Entity revision gap')
    }
    throw new Error('Entity history limit')
  }
  /** Every entity of `kind`, verified. Concurrent lists of one kind share one answer. */
  list(kind: PrivateEntityKind): Promise<Row[]> {
    this.check()
    const pending = this.lists.get(kind)
    if (pending) return pending
    const p = this.listOnce(kind).finally(() => {
      if (this.lists.get(kind) === p) this.lists.delete(kind)
    })
    this.lists.set(kind, p)
    return p
  }
  /** Latest verified value for display: a fresh listed head equal to a verified one is free. */
  async get(kind: PrivateEntityKind, id: string): Promise<Row> {
    this.check()
    const byKind = await this.heads()
    const head = byKind
      ? this.withOwn(kind, byKind.get(kind) ?? []).find((h) => h.id === id)
      : undefined
    return head ? this.atHead(kind, head) : this.read(kind, id)
  }
  private async listOnce(kind: PrivateEntityKind): Promise<Row[]> {
    const byKind = await this.heads()
    if (!byKind) return this.listLegacy(kind)
    const rows: Row[] = []
    for (const head of this.withOwn(kind, byKind.get(kind) ?? []))
      rows.push(await this.atHead(kind, head))
    this.check()
    return rows
  }
  /** Listed heads plus newer ones this session wrote (sorted by id, as the relay lists them). */
  private withOwn(kind: PrivateEntityKind, listed: Head[]): Head[] {
    const out = new Map(listed.map((h) => [h.id, h]))
    for (const h of this.own.values())
      if (h.kind === kind && (out.get(h.id)?.revision ?? 0) < h.revision) out.set(h.id, h)
    return [...out.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  }
  /** The verified row at `head`: memory, then this browser's checkpoint, then the relay. */
  private atHead(kind: PrivateEntityKind, head: Head): Promise<Row> {
    const key = `${kind}/${head.id}`
    const known = this.memo.get(key)
    if (known) {
      if (known.revision > head.revision) throw new Error('Entity listing rollback')
      if (known.revision === head.revision) {
        if (known.digest !== head.digest) throw new Error('Entity head fork')
        return Promise.resolve(structuredClone(this.row(known)))
      }
    }
    const flight = `${key}/${head.revision}`
    const pending = this.inflight.get(flight)
    if (pending) return pending.then((r) => structuredClone(r))
    const p = (async () => {
      const saved = await idb.get<Saved>(this.key(kind, head.id))
      this.check()
      if (saved?.head && saved.content && saved.head.revision >= head.revision) {
        if (saved.head.revision > head.revision) throw new Error('Entity listing rollback')
        if (saved.head.digest !== head.digest) throw new Error('Entity head fork')
        const body = await this.open(saved.content, kind, head.id)
        const row = { id: head.id, kind, revision: head.revision, value: body.value }
        this.remember(row, head.digest)
        return row
      }
      const row = await this.read(kind, head.id)
      if (row.revision < head.revision) throw new Error('Entity listing rollback')
      return row
    })().finally(() => {
      if (this.inflight.get(flight) === p) this.inflight.delete(flight)
    })
    this.inflight.set(flight, p)
    return p.then((r) => structuredClone(r))
  }
  private row(r: Row): Row {
    return { id: r.id, kind: r.kind, revision: r.revision, value: r.value }
  }
  private remember(row: Row, digest: string) {
    if (this.locked) return
    const key = `${row.kind}/${row.id}`
    if ((this.memo.get(key)?.revision ?? 0) <= row.revision)
      this.memo.set(key, { ...structuredClone(row), digest })
  }
  /** One shared all-kinds heads answer (`undefined`: the relay has no batched heads). */
  private heads(): Promise<HeadsByKind | undefined> {
    if (!this.options.transport.heads || this.headsUnsupported) return Promise.resolve(undefined)
    const ttl = this.options.headsTtlMs ?? ENTITY_HEADS_TTL_MS
    const now = this.options.now ?? Date.now
    const s = this.snap
    if (s && ttl > 0 && (s.at === undefined || now() - s.at < ttl)) return s.p
    const entry: { p: Promise<HeadsByKind | undefined>; at?: number } = { p: this.fetchHeads() }
    this.snap = entry
    entry.p.then(
      () => {
        entry.at = now()
      },
      () => {
        if (this.snap === entry) this.snap = undefined
      },
    )
    return entry.p
  }
  private async fetchHeads(): Promise<HeadsByKind | undefined> {
    const heads = this.options.transport.heads!
    const known = this.known
    const items: Head[] = []
    let after: string | undefined
    let version: number | undefined
    for (let n = 0; n < 4096; n++) {
      let raw: unknown
      try {
        raw = await heads({
          ...(after !== undefined ? { after } : known ? { since: known.version } : {}),
        })
      } catch (e) {
        if (after === undefined && isUnprovisioned(e) && this.options.unprovisioned) {
          if (await this.options.unprovisioned()) return new Map()
          throw new Error('Encrypted directory missing at relay')
        }
        // An older relay without the batched route: list per kind from now on.
        if (after === undefined && isUnknownRoute(e)) {
          this.headsUnsupported = true
          return undefined
        }
        throw e
      }
      const page = EntityHeadsPage.parse(raw)
      this.check()
      if (page.unchanged) {
        if (after !== undefined || !known || page.version !== known.version || page.items.length)
          throw new Error('Entity heads substitution')
        return known.byKind
      }
      version ??= page.version
      let last = after ?? ''
      for (const head of page.items) {
        const at = `${head.kind}:${head.id}`
        if (at <= last) throw new Error('Entity listing substitution')
        items.push(head)
        last = at
      }
      if (!page.next) {
        const byKind: HeadsByKind = new Map()
        for (const head of items) {
          const list = byKind.get(head.kind) ?? []
          list.push(head)
          byKind.set(head.kind, list)
        }
        this.known = { version, byKind }
        return byKind
      }
      if (page.next !== last || page.next === after) throw new Error('Entity listing gap')
      after = page.next
    }
    throw new Error('Entity listing limit')
  }
  private async listLegacy(kind: PrivateEntityKind) {
    this.check()
    const rows = []
    let after: string | undefined
    for (let n = 0; n < 4096; n++) {
      let raw: unknown
      try {
        raw = await this.options.transport.list({ kind, ...(after ? { after } : {}) })
      } catch (e) {
        // A fresh account has nothing yet; a relay claim alone never hides a pinned directory.
        if (after === undefined && isUnprovisioned(e) && this.options.unprovisioned) {
          if (await this.options.unprovisioned()) return []
          throw new Error('Encrypted directory missing at relay')
        }
        throw e
      }
      const page = EntityListPage.parse(raw)
      this.check()
      let last = after ?? ''
      for (const head of page.items) {
        if (head.kind !== kind || head.id <= last) throw new Error('Entity listing substitution')
        const row = await this.atHead(kind, head)
        if (row.revision < head.revision) throw new Error('Entity listing rollback')
        rows.push(row)
        last = head.id
      }
      if (!page.next) return rows
      if (page.next !== last || page.next === after) throw new Error('Entity listing gap')
      after = page.next
    }
    throw new Error('Entity listing limit')
  }
  /** Persist the exact randomized ciphertext before send. An uncertain result must retry it. */
  async write(kind: PrivateEntityKind, id: string, value: Record<string, unknown>) {
    this.check()
    const storageKey = this.key(kind, id)
    const before = (await idb.get<Saved>(storageKey)) ?? {}
    let pending = before.pending
    if (pending) {
      const body = await this.open(pending.content, kind, id)
      const retryValue = {
        ...value,
        ...(body.value.updatedAt !== undefined ? { updatedAt: body.value.updatedAt } : {}),
      }
      if (canonicalize(body.value) !== canonicalize(retryValue))
        throw new Error('Retry the pending entity update first')
      value = body.value
    } else {
      const { epoch, key } = await this.options.currentKey()
      const context = {
        version: 1 as const,
        accountId: this.options.accountId,
        epoch,
        runId: `entity:${kind}`,
        objectId: id,
        kind,
        seq: (before.head?.revision ?? 0) + 1,
      }
      const previous = before.head?.digest ?? null
      const body = EntityPlaintext.parse({ version: 1, previous, value })
      const content = EntityContent.parse({
        context,
        box: await sealContent(key, context, utf8(canonicalize(body))),
      })
      if (new TextEncoder().encode(JSON.stringify(content)).byteLength > ENTITY_RECORD_BYTES)
        throw new Error('Entity too large')
      pending = { content, previous }
      this.check()
      await idb.update<Saved>(storageKey, (current) => {
        this.check()
        if (JSON.stringify(current ?? {}) !== JSON.stringify(before))
          throw new Error('Entity checkpoint changed')
        return { ...before, pending }
      })
    }
    const expected: Head = {
      kind,
      id,
      revision: pending.content.context.seq,
      digest: await hashCanonicalB64('viko/entity-revision/v1', pending.content),
    }
    const accepted = EntityHead.parse(await this.options.transport.put(pending))
    this.check()
    if (canonicalize(accepted) !== canonicalize(expected))
      throw new Error('Entity write acknowledgement mismatch')
    await idb.update<Saved>(storageKey, (current) => {
      this.check()
      if (JSON.stringify(current?.pending) !== JSON.stringify(pending))
        throw new Error('Entity checkpoint changed')
      if ((current?.head?.revision ?? 0) > expected.revision) throw new Error('Entity rollback')
      return { head: expected, content: pending.content }
    })
    const row = { id, kind, revision: expected.revision, value }
    this.remember(row, expected.digest)
    this.own.set(`${kind}/${id}`, expected)
    return row
  }
}

/** The relay does not know the route (an older hub than this app). */
const isUnknownRoute = (e: unknown) =>
  e instanceof ContentRequestError &&
  (e.status === 404 || e.status === 405) &&
  e.code !== 'entity_not_found'

/** Authenticated HTTP transport with an actual response-byte cap, independent of Content-Length. */
export function entityTransport(fetcher: import('./api.js').Fetcher, origin = ''): EntityTransport {
  async function request(operation: 'put' | 'list' | 'read' | 'heads', body: unknown) {
    const json = JSON.stringify(body)
    if (new TextEncoder().encode(json).byteLength > 260_000)
      throw new Error('Entity request too large')
    const response = await fetcher(`${origin}/api/content/entity/${operation}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: json,
    })
    if (!response.ok) throw await contentRequestError(response, 'Encrypted work request failed')
    if (!response.body) throw new Error('Encrypted work response missing')
    const reader = response.body.getReader(),
      chunks: Uint8Array[] = []
    let size = 0
    try {
      for (;;) {
        const part = await reader.read()
        if (part.done) break
        size += part.value.byteLength
        const cap =
          operation === 'read'
            ? 16 * ENTITY_RECORD_BYTES + 4096
            : operation === 'heads'
              ? ENTITY_HEADS_PAGE_BYTES
              : 16_384
        if (size > cap) {
          await reader.cancel()
          throw new Error('Entity response too large')
        }
        chunks.push(part.value)
      }
    } finally {
      reader.releaseLock()
    }
    const data = new Uint8Array(size)
    let offset = 0
    for (const chunk of chunks) {
      data.set(chunk, offset)
      offset += chunk.length
    }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(data)) as unknown
  }
  return {
    put: (p) => request('put', p),
    list: (p) => request('list', p),
    read: (p) => request('read', p),
    heads: (p) => request('heads', p),
  }
}
